import { Formik } from 'formik';
import * as Yup from 'yup';
import styles from './CreateUnionForm.module.scss';
import { Input } from 'shared/components/common/Input/Input';
import { IInterestScopes, IOrganisationTypes } from 'interfaces';
import { Button } from 'shared/components/common/Button/Button';
import { TextArea } from 'shared/components/common/TextArea/TextArea';
import { Radio } from 'shared/components/common/Radio/Radio';
import { Select } from 'shared/components/common/Select/Select';
import { useTranslation } from 'react-i18next';

type ICreateUnionFormProps = {
  onFormSubmit: (values: any) => void;
  organisationTypes: IOrganisationTypes[];
  interestScope: IInterestScopes[];
  asyncErrors: { errors?: string[] };
  setAsyncErrors: (values: { errors?: string[] }) => void;
};

export function CreateUnionForm({ onFormSubmit, organisationTypes, interestScope, setAsyncErrors, asyncErrors }: ICreateUnionFormProps) {
  const { t } = useTranslation('createUnion');
  const validationSchema = Yup.object().shape({
    short_name: Yup.string().trim().required(t('errors.required')),
    interests: Yup.array().min(1, t('errors.minOneOption')),
    description: Yup.string().trim().required(t('errors.required')),
    type_name: Yup.string().trim().required(t('errors.required')),
  });

  const defaultFormProps = (name: string, formProps: any) => {
    const { errors, touched, values, setFieldTouched } = formProps;
    return {
      name,
      onTouch: () => {
        setFieldTouched(name);
      },
      required: true,
      value: values[name],
      error: touched[name] && errors[name],
    };
  };

  return (
    <>
      <h1>{t('form.title')}</h1>
      <div className={styles.CreateUnionFormHelperText}>
        <h3>{t('form.enter')}</h3>
      </div>
      <Formik
        initialValues={{ short_name: '', interests: [], description: '', type_name: '' }}
        validationSchema={validationSchema}
        validate={() => {
          return asyncErrors;
        }}
        onSubmit={(values: any) => {
          onFormSubmit(values);
        }}
      >
        {(formProps) => (
          <form
            className={styles.CreateUnionForm}
            onSubmit={(e) => {
              e.preventDefault();
              formProps.validateForm();
            }}
          >
            <Input
              {...defaultFormProps('short_name', formProps)}
              valueChange={(value) => {
                setAsyncErrors({});
                formProps.setFieldValue('short_name', value);
              }}
              label={t('form.form.short_name.label')}
              description={t('form.form.short_name.description')}
              placeholder={t('form.form.short_name.placeholder')}
              dataCy="name-of-union"
            />
            <Select
              {...defaultFormProps('interests', formProps)}
              label={t('form.form.interests.label')}
              multiselect
              description={t('form.form.interests.description')}
              options={interestScope}
              labelKey="name"
              alwaysOpen
              dataCy="interests-select"
              onSelect={(value) => {
                setAsyncErrors({});
                formProps.setFieldValue('interests', value);
              }}
            />
            <TextArea
              {...defaultFormProps('description', formProps)}
              label={t('form.form.description.label')}
              placeholder={t('form.form.description.placeholder')}
              description={t('form.form.description.description')}
              dataCy="areas-of-work"
              valueChange={(value) => formProps.setFieldValue('description', value)}
            />
            <Radio
              {...defaultFormProps('type_name', formProps)}
              valueChange={(value) => formProps.setFieldValue('type_name', value)}
              options={organisationTypes}
              labelKey="name"
              dataCy="org-type"
              label={t('form.form.type_name.label')}
              description={t('form.form.type_name.description')}
            />
            <div className="form-group form-actions">
              <Button dataCy="create-union" onClick={() => formProps.validateForm().then(() => formProps.handleSubmit())} type="submit">
                {t('form.form.submit')}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
}
